import { useMutation, useQuery } from "@tanstack/react-query";
import { ClientOnly, createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { zhCN } from "date-fns/locale";
import {
	AlertCircleIcon,
	BoxIcon,
	FileWarningIcon,
	ImageIcon,
	PlusIcon,
	SquareIcon,
	TerminalIcon,
	XIcon,
} from "lucide-react";
import { useCallback, useState } from "react";

import { OptimizedImage } from "@/components/optimized-image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImagePreview } from "@/components/ui/composed/image-preview";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
	sandboxFileContentQueryOptions,
	sandboxFileDataUrlQueryOptions,
	sandboxFilesQueryOptions,
	sandboxListQueryOptions,
} from "@/hooks/query/sandbox";
import { $createSandbox, $stopSandbox } from "@/server/actions/sandbox";
import type { SandboxTemplateName } from "@/server/sandbox/vercel-sandbox";

import { GhosttyTerminal } from "./-components/ghostty-terminal";
import { SandboxMonacoEditor } from "./-components/monaco-editor";
import { SandboxFileTree } from "./-components/sandbox-file-tree";

export const Route = createFileRoute("/admin/sandbox/")({
	component: RouteComponent,
});

function RouteComponent() {
	const [sandboxId, setSandboxId] = useState<string | null>(null);
	const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);
	const [openTabs, setOpenTabs] = useState<string[]>([]);
	const [selectedTemplate, setSelectedTemplate] =
		useState<SandboxTemplateName>("remotion-video-opencode");

	const isSandboxActive = !!sandboxId;

	const { data: sandboxListData, isLoading: isLoadingList } = useQuery(sandboxListQueryOptions());
	const sandboxes = sandboxListData?.sandboxes ?? [];

	const { mutate: createSandbox, isPending: isCreating } = useMutation({
		mutationKey: ["sandbox", "create"],
		mutationFn: (templateName: SandboxTemplateName) =>
			$createSandbox({ data: { templateName } }),
		onSuccess: (data) => {
			setSandboxId(data.sandboxId);
		},
		meta: {
			error: { message: "Failed to create sandbox" },
		},
	});

	const { mutate: stopSandbox, isPending: isStopping } = useMutation({
		mutationKey: ["sandbox", "stop"],
		mutationFn: () => {
			if (!sandboxId) throw new Error("No sandbox to stop");
			return $stopSandbox({ data: { sandboxId } });
		},
		onSuccess: () => {
			setSandboxId(null);
			setSelectedFilePath(null);
		},
		meta: {
			error: { message: "Failed to stop sandbox" },
		},
	});

	const { data: filesData, isLoading: isLoadingFiles } = useQuery(
		sandboxFilesQueryOptions({ sandboxId: sandboxId ?? undefined }),
	);

	const fileKind = getSandboxFileKind(selectedFilePath);
	const shouldLoadText = fileKind === "text";
	const shouldLoadImage = fileKind === "image";

	const {
		data: fileContentData,
		isLoading: isLoadingContent,
		error: fileContentError,
	} = useQuery(
		sandboxFileContentQueryOptions({
			sandboxId: shouldLoadText ? (sandboxId ?? undefined) : undefined,
			filePath: shouldLoadText ? (selectedFilePath ?? undefined) : undefined,
		}),
	);

	const {
		data: imageData,
		isLoading: isLoadingImage,
		error: imageError,
	} = useQuery(
		sandboxFileDataUrlQueryOptions({
			sandboxId: shouldLoadImage ? (sandboxId ?? undefined) : undefined,
			filePath: shouldLoadImage ? (selectedFilePath ?? undefined) : undefined,
		}),
	);

	const handleFileSelect = useCallback((path: string) => {
		setSelectedFilePath(path);
		setOpenTabs((prev) => (prev.includes(path) ? prev : [...prev, path]));
	}, []);

	const handleCloseTab = useCallback(
		(path: string) => {
			setOpenTabs((prev) => {
				const next = prev.filter((p) => p !== path);
				if (selectedFilePath === path) {
					const closedIndex = prev.indexOf(path);
					const newSelected = next[Math.min(closedIndex, next.length - 1)] ?? null;
					setSelectedFilePath(newSelected);
				}
				return next;
			});
		},
		[selectedFilePath],
	);

	const handleSelectSandbox = useCallback((id: string) => {
		setSandboxId(id);
		setSelectedFilePath(null);
	}, []);

	const handleStartSandbox = useCallback(() => {
		createSandbox(selectedTemplate);
	}, [createSandbox, selectedTemplate]);

	const handleStopSandbox = useCallback(() => {
		stopSandbox();
	}, [stopSandbox]);

	// Extract filename from path for the header
	const selectedFileName = selectedFilePath?.split("/").pop() ?? null;

	return (
		<div className="h-screen w-full">
			<ResizablePanelGroup direction="horizontal">
				<ResizablePanel defaultSize="25%" minSize="15%" maxSize="40%">
					<SandboxSidebar
						sandboxes={sandboxes}
						isLoadingList={isLoadingList}
						isSandboxActive={isSandboxActive}
						isCreating={isCreating}
						isStopping={isStopping}
						isLoadingFiles={isLoadingFiles}
						files={filesData?.files ?? []}
						selectedFilePath={selectedFilePath}
						selectedTemplate={selectedTemplate}
						onTemplateChange={setSelectedTemplate}
						onFileSelect={handleFileSelect}
						onSelectSandbox={handleSelectSandbox}
						onStart={handleStartSandbox}
						onStop={handleStopSandbox}
					/>
				</ResizablePanel>
				<ResizableHandle withHandle />
				<ResizablePanel defaultSize="75%">
					<ResizablePanelGroup direction="vertical">
						<ResizablePanel defaultSize="65%" minSize="20%">
							<div className="flex h-full flex-col">
								{openTabs.length > 0 && (
									<div className="flex items-center border-b overflow-x-auto">
										{openTabs.map((tabPath) => {
											const tabName = tabPath.split("/").pop() ?? tabPath;
											const isActive = tabPath === selectedFilePath;
											return (
												<button
													key={tabPath}
													type="button"
													className={`group flex items-center gap-1.5 border-r px-3 py-1.5 text-sm shrink-0 ${
														isActive
															? "bg-background text-foreground"
															: "bg-muted/50 text-muted-foreground hover:bg-muted"
													}`}
													onClick={() => setSelectedFilePath(tabPath)}
												>
													<span className="font-mono text-xs">
														{tabName}
													</span>
													<span
														className="rounded p-0.5 opacity-0 hover:bg-accent group-hover:opacity-100"
														onClick={(e) => {
															e.stopPropagation();
															handleCloseTab(tabPath);
														}}
														onKeyDown={(e) => {
															if (e.key === "Enter") {
																e.stopPropagation();
																handleCloseTab(tabPath);
															}
														}}
														role="button"
														tabIndex={0}
													>
														<XIcon className="size-3" />
													</span>
												</button>
											);
										})}
									</div>
								)}
								<div className="relative min-h-0 flex-1">
									<ClientOnly
										fallback={
											<div className="flex flex-col gap-2 p-4">
												<Skeleton className="h-4 w-3/4" />
												<Skeleton className="h-4 w-1/2" />
												<Skeleton className="h-4 w-5/6" />
											</div>
										}
									>
										{fileKind === "text" && (
											<SandboxMonacoEditor
												filePath={selectedFilePath}
												content={fileContentData?.content ?? null}
												isLoading={isLoadingContent}
												error={fileContentError}
											/>
										)}
										{fileKind === "image" && (
											<SandboxImagePreview
												fileName={selectedFileName}
												dataUrl={imageData?.dataUrl ?? null}
												isLoading={isLoadingImage}
												error={imageError}
											/>
										)}
										{fileKind === "binary" && <SandboxUnsupportedPreview />}
										{!fileKind && (
											<Empty className="h-full border-0">
												<EmptyHeader>
													<EmptyMedia variant="icon">
														<ImageIcon />
													</EmptyMedia>
													<EmptyTitle>No file selected</EmptyTitle>
													<EmptyDescription>
														Select a file from the tree to view its
														contents
													</EmptyDescription>
												</EmptyHeader>
											</Empty>
										)}
									</ClientOnly>
								</div>
							</div>
						</ResizablePanel>
						{isSandboxActive && (
							<>
								<ResizableHandle withHandle />
								<ResizablePanel defaultSize="35%" minSize="10%">
									<div className="flex h-full flex-col">
										<div className="flex items-center gap-2 border-b px-4 py-2 text-sm text-muted-foreground">
											<TerminalIcon className="size-3.5" />
											<span>Terminal</span>
										</div>
										<div className="min-h-0 flex-1">
											<ClientOnly
												fallback={
													<div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
														<Spinner />
														<span>Loading terminal...</span>
													</div>
												}
											>
												<GhosttyTerminal sandboxId={sandboxId} />
											</ClientOnly>
										</div>
									</div>
								</ResizablePanel>
							</>
						)}
					</ResizablePanelGroup>
				</ResizablePanel>
			</ResizablePanelGroup>
		</div>
	);
}

type SandboxFileKind = "text" | "image" | "binary" | null;

const TEXT_EXTENSIONS = new Set([
	"txt",
	"md",
	"markdown",
	"json",
	"yaml",
	"yml",
	"toml",
	"ini",
	"env",
	"log",
	"csv",
	"ts",
	"tsx",
	"js",
	"jsx",
	"mjs",
	"cjs",
	"cts",
	"mts",
	"css",
	"scss",
	"sass",
	"less",
	"html",
	"htm",
	"xml",
	"sh",
	"bash",
	"zsh",
	"py",
	"rb",
	"go",
	"rs",
	"java",
	"kt",
	"swift",
	"c",
	"h",
	"cpp",
	"hpp",
	"m",
	"mm",
	"sql",
	"graphql",
	"gql",
	"lock",
	"npmrc",
	"gitignore",
]);

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"]);

const TEXT_FILENAMES = new Set(["dockerfile", "makefile", "license"]);

function getSandboxFileKind(filePath: string | null): SandboxFileKind {
	if (!filePath) return null;
	const name = filePath.split("/").pop()?.toLowerCase() ?? "";
	const ext = name.includes(".") ? (name.split(".").pop() ?? "") : "";

	if (IMAGE_EXTENSIONS.has(ext)) return "image";
	if (TEXT_EXTENSIONS.has(ext)) return "text";
	if (TEXT_FILENAMES.has(name)) return "text";
	if (name.startsWith("readme")) return "text";

	return "binary";
}

function SandboxImagePreview({
	fileName,
	dataUrl,
	isLoading,
	error,
}: {
	fileName: string | null;
	dataUrl: string | null;
	isLoading: boolean;
	error?: Error | null;
}) {
	if (error) {
		return (
			<Empty className="h-full border-0">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<AlertCircleIcon />
					</EmptyMedia>
					<EmptyTitle>无法读取图片</EmptyTitle>
					<EmptyDescription>{error.message}</EmptyDescription>
				</EmptyHeader>
			</Empty>
		);
	}

	if (isLoading || !dataUrl) {
		return (
			<div className="flex h-full w-full items-center justify-center p-6">
				<div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
					<Spinner />
					<span>Loading image...</span>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full w-full items-center justify-center p-6">
			<ImagePreview
				src={dataUrl}
				alt={fileName ?? "Image preview"}
				className="max-h-full max-w-full"
			>
				<OptimizedImage
					src={dataUrl}
					alt={fileName ?? "Image preview"}
					layout="constrained"
					width={800}
					height={600}
					className="max-h-[80vh] max-w-[80vw] rounded-md border object-contain shadow-sm"
				/>
			</ImagePreview>
		</div>
	);
}

function SandboxUnsupportedPreview() {
	return (
		<Empty className="h-full border-0">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<FileWarningIcon />
				</EmptyMedia>
				<EmptyTitle>不支持预览该文件</EmptyTitle>
				<EmptyDescription>该文件类型暂不支持在 Sandbox 中预览</EmptyDescription>
			</EmptyHeader>
		</Empty>
	);
}

function formatMemoryLabel(memory?: number | null) {
	if (!memory && memory !== 0) return null;

	// Vercel Sandbox returns memory as MB in practice, but handle bytes defensively.
	const memoryNumber = Number(memory);
	if (Number.isNaN(memoryNumber)) return null;

	const bytesThreshold = 1024 * 1024; // 1 MiB
	if (memoryNumber >= bytesThreshold) {
		const gb = memoryNumber / (1024 * 1024 * 1024);
		if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
		const mb = memoryNumber / (1024 * 1024);
		return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
	}

	if (memoryNumber >= 1024) {
		const gb = memoryNumber / 1024;
		return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
	}

	return `${memoryNumber} MB`;
}

function formatSandboxResources({
	memory,
	vcpus,
}: {
	memory?: number | null;
	vcpus?: number | null;
}) {
	const parts: string[] = [];
	if (typeof vcpus === "number") {
		parts.push(`${vcpus} vCPU${vcpus === 1 ? "" : "s"}`);
	}
	const memoryLabel = formatMemoryLabel(memory);
	if (memoryLabel) parts.push(memoryLabel);
	return parts.join(" · ");
}

const templateLabels: Record<SandboxTemplateName, string> = {
	"claude-code": "Claude Code",
	"studyos-code-playground": "StudyOS Code Playground",
	"remotion-video-opencode": "Remotion Video (OpenCode)",
	"remotion-video-claude": "Remotion Video (Claude Code)",
};

type SandboxSidebarProps = {
	sandboxes: {
		id: string;
		createdAt: number;
		runtime: string;
		timeout: number;
		memory?: number | null;
		vcpus?: number | null;
	}[];
	isLoadingList: boolean;
	isSandboxActive: boolean;
	isCreating: boolean;
	isStopping: boolean;
	isLoadingFiles: boolean;
	files: string[];
	selectedFilePath: string | null;
	selectedTemplate: SandboxTemplateName;
	onTemplateChange: (template: SandboxTemplateName) => void;
	onFileSelect: (path: string) => void;
	onSelectSandbox: (id: string) => void;
	onStart: () => void;
	onStop: () => void;
};

function SandboxSidebar({
	sandboxes,
	isLoadingList,
	isSandboxActive,
	isCreating,
	isStopping,
	isLoadingFiles,
	files,
	selectedFilePath,
	selectedTemplate,
	onTemplateChange,
	onFileSelect,
	onSelectSandbox,
	onStart,
	onStop,
}: SandboxSidebarProps) {
	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center justify-between border-b px-3 py-2">
				<div className="flex items-center gap-2 text-sm font-medium">
					<BoxIcon className="size-4" />
					<span>Sandbox</span>
				</div>
				<div className="flex items-center gap-1">
					{isSandboxActive && (
						<Button variant="ghost" size="xs" onClick={onStop} disabled={isStopping}>
							{isStopping ? <Spinner /> : <SquareIcon className="size-3" />}
							Stop
						</Button>
					)}
					<Button variant="ghost" size="xs" onClick={onStart} disabled={isCreating}>
						{isCreating ? <Spinner /> : <PlusIcon className="size-3" />}
						New
					</Button>
				</div>
			</div>

			{!isSandboxActive && !isCreating && (
				<div className="border-b px-3 py-2">
					<Select
						value={selectedTemplate}
						onValueChange={(val) => onTemplateChange(val as SandboxTemplateName)}
					>
						<SelectTrigger className="w-full" size="sm">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{Object.entries(templateLabels).map(([value, label]) => (
								<SelectItem key={value} value={value}>
									{label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			<div className="flex-1 overflow-auto">
				{/* Sandbox list when no sandbox is selected */}
				{!isSandboxActive && !isCreating && (
					<div className="flex flex-col">
						{isLoadingList ? (
							<div className="flex flex-col gap-2 p-3">
								<Skeleton className="h-10 w-full" />
								<Skeleton className="h-10 w-full" />
							</div>
						) : sandboxes.length === 0 ? (
							<Empty className="h-full border-0">
								<EmptyHeader>
									<EmptyMedia variant="icon">
										<BoxIcon />
									</EmptyMedia>
									<EmptyTitle>没有运行中的 Sandbox</EmptyTitle>
									<EmptyDescription>
										点击 New 创建一个新的 Sandbox
									</EmptyDescription>
								</EmptyHeader>
							</Empty>
						) : (
							<ul className="flex flex-col py-1">
								{sandboxes.map((sandbox) => {
									const resourceLabel = formatSandboxResources(sandbox);

									return (
										<li key={sandbox.id}>
											<button
												type="button"
												className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent transition-colors"
												onClick={() => onSelectSandbox(sandbox.id)}
											>
												<span className="size-2 shrink-0 rounded-full bg-green-500" />
												<div className="flex flex-col gap-0.5 overflow-hidden">
													<div className="flex items-center gap-2">
														<span className="truncate font-medium">
															{sandbox.id}
														</span>
														{resourceLabel ? (
															<Badge
																className="shrink-0 font-mono text-[11px]"
																variant="secondary"
															>
																{resourceLabel}
															</Badge>
														) : null}
													</div>
													<div className="flex items-center gap-2 text-xs text-muted-foreground">
														<span>·</span>
														<span>
															{Math.round(sandbox.timeout / 60_000)}
															min
														</span>
														<span>·</span>
														<span>
															{formatDistanceToNow(
																new Date(sandbox.createdAt),
																{
																	addSuffix: true,
																	locale: zhCN,
																},
															)}
														</span>
													</div>
												</div>
											</button>
										</li>
									);
								})}
							</ul>
						)}
					</div>
				)}

				{isCreating && (
					<div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
						<Spinner />
						<span>Starting sandbox...</span>
					</div>
				)}

				{isSandboxActive && isLoadingFiles && (
					<div className="flex flex-col gap-2 p-3">
						<Skeleton className="h-5 w-3/4" />
						<Skeleton className="ml-4 h-5 w-1/2" />
						<Skeleton className="ml-4 h-5 w-2/3" />
						<Skeleton className="h-5 w-1/2" />
						<Skeleton className="ml-4 h-5 w-3/5" />
					</div>
				)}

				{isSandboxActive && !isLoadingFiles && files.length > 0 && (
					<SandboxFileTree
						files={files}
						selectedPath={selectedFilePath ?? undefined}
						onSelect={onFileSelect}
					/>
				)}

				{isSandboxActive && !isLoadingFiles && files.length === 0 && (
					<Empty className="h-full border-0">
						<EmptyHeader>
							<EmptyTitle>No files found</EmptyTitle>
							<EmptyDescription>The sandbox filesystem is empty</EmptyDescription>
						</EmptyHeader>
					</Empty>
				)}
			</div>
		</div>
	);
}
